import { useMemo } from 'react';
import ReactECharts, { AXIS_TEXT, GRID_LINE } from 'pages/analytics/charts/echarts';
import type { LookupResult } from './api';
import { dm, fmtNum, fmtShort, monthText } from './format';

// 5 người đóng góp nhiều nhất + "Còn lại" (xám) — màu theo bảng màu trang Phân tích.
const COLORS = ['#C2337D', '#1F5FA8', '#0F7A46', '#B7791F', '#6B46C1'];
const REST = '#C9C2CE';
const TOP = 5;

/**
 * Cột xếp chồng: kỳ một tháng có file ngày → theo ngày; nhiều tháng → theo tháng. Mỗi cột = tổng KC của
 * cả tập ở mốc đó, chia 5 Creator nhiều KC nhất của cả kỳ + phần còn lại. Không có gì để vẽ → null.
 */
export default function StackChart({ result }: { result: LookupResult }) {
  const option = useMemo(() => {
    const multi = result.segments.length > 1;
    const byDay = !multi && result.daily.length > 1;
    if (!multi && !byDay) return null;
    const keys = multi ? result.segments.map((s) => s.key) : result.daily.map((d) => d.date);
    const labels = multi ? keys.map((k) => monthText(k).replace(/\/\d{4}$/, '')) : keys.map(dm);
    const valueOf = (r: LookupResult['rows'][number], k: string) =>
      multi ? r.bySegment[k] || 0 : r.daily[k] || 0;
    const top = [...result.rows].filter((r) => r.diamonds > 0).sort((a, b) => b.diamonds - a.diamonds).slice(0, TOP);
    const topIds = new Set(top.map((r) => r.uid));
    const totals = keys.map((k) => result.rows.reduce((a, r) => a + valueOf(r, k), 0));
    const series = [
      ...top.map((r, i) => ({
        name: r.username ? `@${r.username}` : r.uid,
        type: 'bar',
        stack: 'kc',
        data: keys.map((k) => valueOf(r, k)),
        itemStyle: { color: COLORS[i] },
        barMaxWidth: 28,
        emphasis: { focus: 'series' },
      })),
      {
        name: 'Còn lại',
        type: 'bar',
        stack: 'kc',
        data: keys.map(
          (k, i) => totals[i] - result.rows.filter((r) => topIds.has(r.uid)).reduce((a, r) => a + valueOf(r, k), 0)
        ),
        itemStyle: { color: REST, borderRadius: [3, 3, 0, 0] },
        barMaxWidth: 28,
      },
    ];
    return {
      grid: { left: 52, right: 12, top: 56, bottom: 26 },
      legend: { top: 0, type: 'scroll', textStyle: { color: AXIS_TEXT, fontSize: 11 }, itemWidth: 10, itemHeight: 10 },
      tooltip: {
        trigger: 'axis',
        axisPointer: { type: 'shadow' },
        valueFormatter: (v: number) => `${fmtNum(v)} KC`,
      },
      xAxis: {
        type: 'category',
        data: labels,
        axisLine: { lineStyle: { color: GRID_LINE } },
        axisLabel: { color: AXIS_TEXT },
      },
      yAxis: {
        type: 'value',
        splitLine: { lineStyle: { color: GRID_LINE } },
        axisLabel: { color: AXIS_TEXT, formatter: (v: number) => fmtShort(v) },
      },
      series,
    };
  }, [result]);

  if (!option) return null;
  return <ReactECharts option={option} style={{ height: 240 }} notMerge />;
}
